import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { VIEW } from "const"
import { OkrObjective, OkrKeyResult } from "server/models"
import { okrKeyResultsRoute } from "client/parts/routes"
import {
    StoreType,
    setOkrObjectivePopupMenuTarget,
    setOkrObjectivePopupMenuShow,
    setOkrObjectiveActive,
    addModal,
} from "client/store"
import {
    Link,
    UserIcon,
    DateFormatter,
    TableColumn,
    Table,
    TablePopover,
    TablePopoverData,
    TableMenu,
} from "client/components"
import { Confidence, ProgressPercents } from "client/pages/okr/styled"

const Columns = () => {
    const objectiveBaseRoute = useSelector((state: StoreType) => state.okrObjective.baseRoute)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)
    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)
    const keyResultIds = useSelector((state: StoreType) => state.okrKeyResult.keyResultIds)
    const updateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const showContextMenu = (
        e: React.MouseEvent,
        active: OkrObjective,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setOkrObjectivePopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setOkrObjectivePopupMenuShow(true))
        dispatch(setOkrObjectiveActive(active))
    }

    const keyResultColumns: TableColumn[] = [
        {
            field: "title",
            cellMinWidth: "200px",
            cellMaxWidth: "350px",
            formatter: (keyResult: OkrKeyResult) => {
                const baseRoute = `${objectiveBaseRoute}${objectiveRoute}/${VIEW}/${keyResult.objectiveId}`
                const href = `${baseRoute}${okrKeyResultsRoute}/${VIEW}/${keyResult.id}`
                return (
                    <Link
                        href={href}
                        onClick={() => dispatch(addModal({ url: href }))}
                    >
                        {keyResult.title}
                    </Link>
                )
            },
        },
        {
            field: "owners",
            cellMinWidth: "150px",
            formatter: (keyResult: OkrKeyResult) => {
                if (!keyResult.owners) return null

                const dataList: TablePopoverData[] = []
                keyResult.owners.forEach((row) => {
                    const user = userList[row.userId]
                    dataList.push({
                        id: `${user.id}_${keyResult.id}`,
                        title: <UserIcon user={user} size="sm" />,
                    })
                })

                return (
                    <TablePopover
                        id={`keyResultUsers${keyResult.id}`}
                        dataList={dataList}
                        placement="right"
                        listStyle={false}
                        title={lang.owners}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "confidence",
            cellMinWidth: "80px",
            formatter: (keyResult: OkrKeyResult) => {
                const krUpdates = updateList[keyResult.id]
                const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
                if (lastUpdate) {
                    return (
                        <Confidence confidenceLevel={lastUpdate.confidenceLevel}>
                            {lang[lastUpdate.confidenceLevel]}
                        </Confidence>
                    )
                }
                return null
            },
        },
        {
            field: "progress",
            cellMinWidth: "80px",
            formatter: (keyResult: OkrKeyResult) => {
                const krUpdates = updateList[keyResult.id]
                const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
                if (lastUpdate) return <ProgressPercents percents={lastUpdate.progress}>{`${lastUpdate.progress}%`}</ProgressPercents>
                return <ProgressPercents percents={0}>0%</ProgressPercents>
            },
        },
    ]

    const columns: TableColumn[] = [
        {
            field: "menu",
            formatter: (objective: OkrObjective) => <TableMenu onClick={(e) => showContextMenu(e, objective)} />,
        },
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "title",
            header: lang.title,
            sortable: true,
            cellMinWidth: "250px",
            cellMaxWidth: "400px",
            formatter: (objective: OkrObjective) => {
                const href = `${objectiveBaseRoute}${objectiveRoute}/${VIEW}/${objective.id}`
                return (
                    <Link
                        href={href}
                        onClick={() => dispatch(addModal({ url: href }))}
                    >
                        {objective.title}
                    </Link>
                )
            },
        },
        {
            field: "keyResults",
            header: lang.keyResults,
            cellMinWidth: "300px",
            cellMaxWidth: "600px",
            formatter: (objective: OkrObjective) => {
                const krIds = keyResultIds[objective.id] || []
                return (
                    <Table
                        columns={keyResultColumns}
                        dataList={keyResultList}
                        dataIds={krIds}
                        rowSize="fit"
                        showTitle={false}
                        showBottom={false}
                        showScroll={false}
                    />
                )
            },
        },
        {
            field: "progress",
            header: lang.progress,
            cellMinWidth: "80px",
            formatter: (objective: OkrObjective) => {
                const krIds = keyResultIds[objective.id] || []
                let progress = 0

                krIds.forEach((id) => {
                    const krUpdates = updateList[id]
                    const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
                    if (lastUpdate) progress += lastUpdate.progress
                })

                if (krIds.length > 0) progress = Math.round(progress / krIds.length)
                else progress = 0

                return (
                    <ProgressPercents
                        percents={progress}
                        style={{
                            fontWeight: "600",
                            fontSize: "17px",
                        }}
                    >
                        {`${progress}%`}
                    </ProgressPercents>
                )
            },
        },
        {
            field: "description",
            header: lang.description,
            sortable: true,
            cellMinWidth: "250px",
            cellMaxWidth: "400px",
        },
        {
            field: "owners",
            header: lang.owners,
            cellMinWidth: "150px",
            formatter: (objective: OkrObjective) => {
                if (!objective.owners) return null

                const dataList: TablePopoverData[] = []
                objective.owners.forEach((row) => {
                    const user = userList[row.userId]
                    dataList.push({
                        id: `${user.id}_${objective.id}`,
                        title: <UserIcon user={user} size="sm" />,
                    })
                })

                return (
                    <TablePopover
                        id={`objectiveUsers${objective.id}`}
                        dataList={dataList}
                        placement="right"
                        listStyle={false}
                        title={lang.owners}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (objective: OkrObjective) => <DateFormatter date={objective.createdAt} />,
        },
    ]

    return columns
}
export default Columns
